import * as React from 'react'
import { Star } from 'lucide-react'

import { cn } from '../../lib/utils'

/**
 * A node's card, as both the app and the share viewer draw it.
 *
 * The chrome — the border and lift, the star, the name in the node's own colour,
 * the headline cost — is the whole of a node's identity at a glance, and it used
 * to exist twice: once on the canvas and once, approximately, in the viewer. The
 * two drifted, which is how the viewer ended up wearing `shadow-sm`, a class the
 * app had removed as non-standard. One implementation, two consumers.
 *
 * The body is a slot: the canvas puts a preview of the last thing said there, the
 * viewer puts the shared conversation. What they agree on is the frame around it.
 *
 * `scale` is optional and for the canvas only: dividing the header's sizes by the
 * zoom holds it at a constant on-screen size, so a node stays legible from across
 * a plane. The viewer draws at 1 and never passes it.
 */
export interface AirNodeCardProps {
  /**
   * The name on the pill. Absent draws no pill — a shared node has no name of
   * its own, and an empty one reads as a bug rather than as an absence.
   */
  title?: string
  /** The node's own header colour. Absent falls back to the accent. */
  color?: string
  /** Formatted headline cost, e.g. "$30.53". Absent hides it. */
  cost?: string
  starred?: boolean
  /** A turn is in flight — worth seeing from a distance. */
  running?: boolean
  /** Canvas zoom, so the header can hold a constant size on screen. Default 1. */
  scale?: number
  width?: number | string
  height?: number | string
  className?: string
  children?: React.ReactNode
}

export function AirNodeCard({
  title,
  color,
  cost,
  starred,
  running,
  scale = 1,
  width,
  height,
  className,
  children,
}: AirNodeCardProps) {
  // World px that render as a fixed number of screen px. Capped against the card
  // so a deep zoom-out cannot grow the header past what holds it — and capped
  // gently enough that the name keeps its room beside the price.
  const numericWidth = typeof width === 'number' ? width : 560
  const px = (screenPx: number) =>
    scale === 1 ? screenPx : Math.min(numericWidth / 14, screenPx / Math.max(scale, 0.02))
  const gap = px(7)

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl border border-border bg-card air-node-shadow',
        className,
      )}
      style={{ width, height, padding: px(12), gap }}
    >
      {/* The name gets its own line, so the price can never squeeze it away. */}
      {(title || starred || running) && (
      <div className="flex min-w-0 items-center" style={{ gap }}>
        {starred && (
          <Star
            className="shrink-0 fill-amber-400 text-amber-400"
            style={{ width: px(14), height: px(14) }}
          />
        )}
        {title && (
          <span
            className={cn(
              'min-w-0 truncate rounded-full font-medium leading-none text-white',
              !color && 'bg-accent',
            )}
            style={{
              fontSize: px(13),
              padding: `${px(5)}px ${px(9)}px`,
              ...(color ? { backgroundColor: color } : {}),
            }}
          >
            {title}
          </span>
        )}
        {running && (
          <span
            className="ml-auto shrink-0 rounded-full bg-accent"
            style={{ width: px(9), height: px(9) }}
            aria-hidden="true"
          />
        )}
      </div>
      )}

      {cost && (
        <span
          className="shrink-0 font-semibold leading-none tabular-nums text-foreground"
          style={{ fontSize: px(19) }}
        >
          {cost}
        </span>
      )}

      {children}
    </div>
  )
}
